// src/components/Admin/UsersManagement.jsx
import React, { useState, useEffect } from 'react';
import UserDetailModal from './UserDetailModal';
import { doc, getDoc, collection, getDocs } from 'firebase/firestore';
import { db } from '../../services/firebase';

const UsersManagement = ({ users, onDeleteUser }) => {
    const [searchTerm, setSearchTerm] = useState('');
    const [sortField, setSortField] = useState('createdAt');
    const [sortDirection, setSortDirection] = useState('desc');
    const [selectedUser, setSelectedUser] = useState(null);
    const [showUserModal, setShowUserModal] = useState(false);
    const [loadingUser, setLoadingUser] = useState(false);
    const [candidatesData, setCandidatesData] = useState({});

    // Fetch all candidates data to merge with users
    useEffect(() => {
        const loadCandidates = async () => {
            try {
                const querySnapshot = await getDocs(collection(db, 'candidates'));
                const data = {};
                querySnapshot.forEach((doc) => {
                    data[doc.id] = doc.data();
                });
                setCandidatesData(data);
            } catch (error) {
                console.error("Error loading candidates:", error);
            }
        };
        loadCandidates();
    }, []);

    const handleSort = (field) => {
        if (sortField === field) {
            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
        } else {
            setSortField(field);
            setSortDirection('asc');
        }
    };

    const getSortIcon = (field) => {
        if (sortField !== field) return '↕️';
        return sortDirection === 'asc' ? '↑' : '↓';
    };

    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-ZA', { 
            year: 'numeric', 
            month: 'short', 
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    // Merge user data with candidates data for table display
    const getMergedUsers = () => {
        return users.map(user => {
            const candidate = candidatesData[user.id] || {};
            return {
                ...user,
                // Merge fields from candidates
                gender: candidate.gender || user.gender || 'Not specified',
                phoneNumber: candidate.phoneNumber || user.phoneNumber,
                city: candidate.city || user.city,
                country: candidate.country || user.country,
                dateOfBirth: candidate.dateOfBirth || user.dateOfBirth,
                lookingFor: candidate.lookingFor || user.lookingFor,
                preferredJobLocation: candidate.preferredJobLocation || user.preferredJobLocation,
                desiredSalary: candidate.desiredSalary || user.desiredSalary,
                professionalSummary: candidate.professionalSummary || user.professionalSummary,
                workExperience: candidate.workExperience || user.workExperience || [],
                education: candidate.education || user.education || [],
                skills: candidate.skills || user.skills || [],
                languages: candidate.languages || user.languages || [],
                linkedin: candidate.linkedin || user.linkedin,
                portfolio: candidate.portfolio || user.portfolio,
                profileCompleted: candidate.profileCompleted || user.profileCompleted || false,
                // User data takes priority for these
                firstName: user.firstName || candidate.firstName,
                lastName: user.lastName || candidate.lastName,
                email: user.email || candidate.email,
                createdAt: user.createdAt || candidate.createdAt,
                role: user.role || candidate.role || 'candidate',
                userType: user.userType || candidate.userType,
                status: user.status || candidate.status || 'active',
            };
        });
    };

    const getSortedUsers = () => {
        const mergedUsers = getMergedUsers();
        let sorted = [...mergedUsers];
        sorted.sort((a, b) => {
            let aVal = a[sortField] || '';
            let bVal = b[sortField] || '';
            
            if (sortField === 'createdAt') {
                aVal = new Date(aVal);
                bVal = new Date(bVal);
            } else if (sortField === 'firstName') {
                aVal = (a.firstName || '').toLowerCase();
                bVal = (b.firstName || '').toLowerCase();
            } else if (sortField === 'lastName') {
                aVal = (a.lastName || '').toLowerCase();
                bVal = (b.lastName || '').toLowerCase();
            } else if (sortField === 'email') {
                aVal = (a.email || '').toLowerCase();
                bVal = (b.email || '').toLowerCase();
            } else if (sortField === 'gender') {
                aVal = (a.gender || '').toLowerCase();
                bVal = (b.gender || '').toLowerCase();
            } else if (typeof aVal === 'string') {
                aVal = aVal.toLowerCase();
                bVal = bVal.toLowerCase();
            }
            
            if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
            if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
            return 0;
        });
        return sorted;
    };

    const filteredUsers = getSortedUsers().filter(user => {
        const fullName = `${user.firstName || ''} ${user.lastName || ''}`.toLowerCase();
        const searchLower = searchTerm.toLowerCase();
        return fullName.includes(searchLower) ||
            user.email?.toLowerCase().includes(searchLower) ||
            user.idNumber?.includes(searchTerm);
    });

    const handleViewUser = async (user) => {
        setLoadingUser(true);
        try {
            // Fetch the full candidate data from candidates collection
            const candidateDocRef = doc(db, 'candidates', user.id);
            const candidateDocSnap = await getDoc(candidateDocRef);
            const candidateData = candidateDocSnap.exists() ? candidateDocSnap.data() : {};
            
            // Merge user data with candidate data
            const mergedUser = {
                ...user,
                ...candidateData,
                // Ensure user data takes priority for these fields
                firstName: user.firstName || candidateData.firstName,
                lastName: user.lastName || candidateData.lastName,
                email: user.email || candidateData.email,
                createdAt: user.createdAt || candidateData.createdAt,
            };
            
            setSelectedUser(mergedUser);
            setShowUserModal(true);
        } catch (error) {
            console.error("Error loading user details:", error);
            alert('Error loading user details. Please try again.');
        } finally {
            setLoadingUser(false);
        }
    };

    const closeUserModal = () => {
        setShowUserModal(false);
        setSelectedUser(null);
    };

    return (
        <div>
            <h2 className="text-2xl font-bold text-gradpath-dark mb-6 flex items-center gap-2">
                <i className="fas fa-users text-gradpath-green"></i>
                User Records
            </h2>

            {/* Search */}
            <div className="bg-white rounded-xl p-4 mb-6">
                <input
                    type="text"
                    placeholder="Search by name, email, or ID number..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-lg focus:border-gradpath-green outline-none"
                />
            </div>

            {/* Users Table */}
            <div className="bg-white rounded-xl shadow-md overflow-x-auto">
                <table className="w-full min-w-[700px]">
                    <thead className="bg-gray-50">
                        <tr>
                            <th 
                                className="px-4 py-3 text-left text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                                onClick={() => handleSort('firstName')}
                            >
                                Name {getSortIcon('firstName')}
                            </th>
                            <th 
                                className="px-4 py-3 text-left text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                                onClick={() => handleSort('lastName')}
                            >
                                Surname {getSortIcon('lastName')}
                            </th>
                            <th 
                                className="px-4 py-3 text-left text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                                onClick={() => handleSort('email')}
                            >
                                Email {getSortIcon('email')}
                            </th>
                            <th 
                                className="px-4 py-3 text-left text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                                onClick={() => handleSort('gender')}
                            >
                                Gender {getSortIcon('gender')}
                            </th>
                            <th 
                                className="px-4 py-3 text-left text-sm font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                                onClick={() => handleSort('createdAt')}
                            >
                                Date Joined {getSortIcon('createdAt')}
                            </th>
                            <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {filteredUsers.map(user => (
                            <tr key={user.id} className="border-t border-gray-200 hover:bg-gray-50">
                                <td className="px-4 py-3 text-sm">{user.firstName || '-'}</td>
                                <td className="px-4 py-3 text-sm">{user.lastName || '-'}</td>
                                <td className="px-4 py-3 text-sm">{user.email || '-'}</td>
                                <td className="px-4 py-3 text-sm">
                                    <span className="px-2 py-1 rounded-full text-xs bg-gray-100 text-gray-600">
                                        {user.gender || 'Not specified'}
                                    </span>
                                </td>
                                <td className="px-4 py-3 text-sm">{formatDate(user.createdAt)}</td>
                                <td className="px-4 py-3 text-sm">
                                    <button
                                        onClick={() => handleViewUser(user)}
                                        className="text-gradpath-green hover:text-gradpath-dark mr-3"
                                        disabled={loadingUser}
                                    >
                                        {loadingUser ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-eye"></i>}
                                    </button>
                                    <button
                                        onClick={() => onDeleteUser(user.id)}
                                        className="text-red-600 hover:text-red-800"
                                    >
                                        <i className="fas fa-trash"></i>
                                    </button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {filteredUsers.length === 0 && (
                    <div className="text-center py-8 text-gray-500">
                        No users found
                    </div>
                )}
            </div>

            {/* User Detail Modal */}
            <UserDetailModal
                user={selectedUser}
                isOpen={showUserModal}
                onClose={closeUserModal}
            />
        </div>
    );
};

export default UsersManagement;